import React, { useState, useEffect } from 'react';
import { X, User, Building2, Link as LinkIcon, Unlink, AlertCircle, CheckCircle, Loader2, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from './ui/Button';
import { getClients, updateUserClientAssociation, removeUserClientAssociation } from '../lib/api';

interface Client {
  id: string;
  name: string;
  company_name: string | null;
}

interface ClientAssociation {
  client_id: string;
  clients: Client;
}

interface UserWithClient {
  user_id: string;
  role: string;
  email: string | null;
  sales_name: string | null;
  client_id: string | null;
  clients: Client | null;
  client_associations?: ClientAssociation[];
}

interface AssociateClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserWithClient | null;
  onSuccess: () => void;
}

const AssociateClientModal: React.FC<AssociateClientModalProps> = ({
  isOpen,
  onClose,
  user,
  onSuccess
}) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [selectedAssociationsToRemove, setSelectedAssociationsToRemove] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingClients, setIsFetchingClients] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen && user) {
      fetchClients();
      setSelectedClientIds([]);
      setSelectedAssociationsToRemove([]);
      setError(null);
      setSuccess(null);
    }
  }, [isOpen, user]);

  const fetchClients = async () => {
    try {
      setIsFetchingClients(true);
      const data = await getClients({ limit: 1000 });
      setClients(data);
    } catch (err: any) {
      console.error('Error fetching clients:', err);
      setError('Erreur lors du chargement des clients');
    } finally {
      setIsFetchingClients(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) return;

    if (selectedClientIds.length === 0 && selectedAssociationsToRemove.length === 0) {
      setError('Veuillez sélectionner au moins un client à associer ou à supprimer');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let successMessages: string[] = [];

      // Ajouter les nouvelles associations
      for (const clientId of selectedClientIds) {
        const alreadyAssociated = user.client_associations?.some(assoc => assoc.client_id === clientId);
        if (!alreadyAssociated) {
          await updateUserClientAssociation(user.user_id, clientId);
          const client = clients.find(c => c.id === clientId);
          if (client) {
            successMessages.push(`"${client.name}" associé`);
          }
        }
      }

      // Supprimer les associations sélectionnées
      for (const clientId of selectedAssociationsToRemove) {
        await removeUserClientAssociation(user.user_id, clientId);
        const client = clients.find(c => c.id === clientId);
        if (client) {
          successMessages.push(`"${client.name}" supprimé`);
        }
      }

      setSuccess(successMessages.join(', '));

      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err: any) {
      console.error('Error updating associations:', err);
      setError(err.message || 'Erreur lors de la mise à jour des associations');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAssociationSelection = (clientId: string) => {
    setSelectedAssociationsToRemove(prev => {
      if (prev.includes(clientId)) {
        return prev.filter(id => id !== clientId);
      }
      return [...prev, clientId];
    });
  };

  const toggleClientSelection = (clientId: string) => {
    setSelectedClientIds(prev => {
      if (prev.includes(clientId)) {
        return prev.filter(id => id !== clientId);
      }
      return [...prev, clientId];
    });
  };

  const handleRemoveSelectedAssociations = () => {
    // Les suppressions seront traitées dans handleSubmit
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (client.company_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen || !user) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={onClose}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            >
              <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <LinkIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      Associer un client
                    </h2>
                    <p className="text-purple-100 text-sm">
                      Gérer l'association client pour cet utilisateur
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                <div className="mb-6 bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                      <span className="text-white font-semibold text-lg">
                        {(user.email || 'U').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4 text-gray-500" />
                        <span className="font-semibold text-gray-900">{user.email}</span>
                      </div>
                      <div className="mt-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
                          {user.role === 'admin' ? 'Administrateur' : user.role === 'client' ? 'Client' : user.role}
                        </span>
                      </div>
                    </div>
                  </div>

                  {user.client_associations && user.client_associations.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-gray-500">
                          Clients actuellement associés ({user.client_associations.length})
                        </p>
                        {selectedAssociationsToRemove.length > 0 && (
                          <span className="text-xs font-medium text-red-600">
                            {selectedAssociationsToRemove.length} sélectionné(s)
                          </span>
                        )}
                      </div>
                      <div className="space-y-2">
                        {user.client_associations.map((assoc) => (
                          <label
                            key={assoc.client_id}
                            className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                              selectedAssociationsToRemove.includes(assoc.client_id)
                                ? 'bg-red-50 border-2 border-red-300'
                                : 'bg-gray-50 border-2 border-transparent hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={selectedAssociationsToRemove.includes(assoc.client_id)}
                                onChange={() => toggleAssociationSelection(assoc.client_id)}
                                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                              />
                              <Building2 className={`h-4 w-4 ${
                                selectedAssociationsToRemove.includes(assoc.client_id) ? 'text-red-500' : 'text-green-500'
                              }`} />
                              <div>
                                <span className="text-sm font-medium text-gray-900">{assoc.clients.name}</span>
                                {assoc.clients.company_name && (
                                  <span className="text-xs text-gray-500 ml-2">({assoc.clients.company_name})</span>
                                )}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center"
                  >
                    <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                    {error}
                  </motion.div>
                )}

                {success && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center"
                  >
                    <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                    {success}
                  </motion.div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Ajouter des clients
                        </label>
                        {selectedClientIds.length > 0 && (
                          <span className="text-xs font-medium text-purple-600">
                            {selectedClientIds.length} sélectionné(s)
                          </span>
                        )}
                      </div>
                      <input
                        type="text"
                        placeholder="Rechercher un client..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent mb-2"
                      />
                      <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                        {isFetchingClients ? (
                          <div className="flex justify-center items-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                          </div>
                        ) : filteredClients.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            {searchTerm ? 'Aucun client trouvé' : 'Aucun client disponible'}
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-200">
                            {filteredClients.map((client) => {
                              const isAlreadyAssociated = user.client_associations?.some(assoc => assoc.client_id === client.id);
                              const isSelected = selectedClientIds.includes(client.id);
                              return (
                                <label
                                  key={client.id}
                                  className={`flex items-center p-3 cursor-pointer transition-colors ${
                                    isAlreadyAssociated
                                      ? 'bg-gray-100 opacity-60 cursor-not-allowed'
                                      : isSelected
                                      ? 'bg-purple-50 hover:bg-purple-100'
                                      : 'hover:bg-gray-50'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    disabled={isAlreadyAssociated}
                                    onChange={() => !isAlreadyAssociated && toggleClientSelection(client.id)}
                                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                  />
                                  <div className="ml-3 flex-1">
                                    <div className="text-sm font-medium text-gray-900">{client.name}</div>
                                    {client.company_name && (
                                      <div className="text-xs text-gray-500">{client.company_name}</div>
                                    )}
                                  </div>
                                  {isAlreadyAssociated && (
                                    <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">Déjà associé</span>
                                  )}
                                  {isSelected && !isAlreadyAssociated && (
                                    <CheckCircle className="h-5 w-5 text-purple-600" />
                                  )}
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                        <div className="text-sm text-blue-700">
                          <p className="font-medium mb-1">Information</p>
                          <p>
                            Vous pouvez sélectionner plusieurs clients à associer et/ou supprimer en même temps.
                            L'utilisateur aura accès aux données de tous les clients associés.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={onClose}
                      disabled={isLoading}
                    >
                      Annuler
                    </Button>
                    <Button
                      type="submit"
                      disabled={isLoading || (selectedClientIds.length === 0 && selectedAssociationsToRemove.length === 0)}
                      icon={isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LinkIcon className="h-4 w-4" />}
                      className="bg-purple-600 hover:bg-purple-700 focus:ring-purple-500"
                    >
                      {isLoading
                        ? 'Traitement en cours...'
                        : selectedClientIds.length > 0 && selectedAssociationsToRemove.length > 0
                        ? 'Appliquer les modifications'
                        : selectedClientIds.length > 0
                        ? `Associer ${selectedClientIds.length} client(s)`
                        : `Supprimer ${selectedAssociationsToRemove.length} client(s)`
                      }
                    </Button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AssociateClientModal;
